import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { apiService } from '../services/api';
import { authUtils } from '../services/auth';
import Header from '../components/common/Header';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Button from '../components/common/Button';
import { Competition } from '../types/interfaces';
import RelativeTime from '../components/common/RelativeTime';
import './Submission.css';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

interface SubmissionData {
  url: string;
  version: number;
  username?: string;
  team_name?: string;
  team_lead_username?: string;
  manifest?: MultiManifestData;
}

interface FinalistConsentEntry {
  rank: number;
  consent_status: 'pending' | 'granted' | 'denied' | 'revoked' | string;
  can_update_consent: boolean;
}

interface MultiArtifact {
  original_name?: string;
  stored_name?: string;
  content_type?: string;
  size_bytes?: number;
  object_key?: string;
  url?: string;
}

interface MultiFieldEntry {
  id?: string;
  label?: string;
  type?: string;
  value?: unknown;
}

interface MultiManifestData {
  schema?: string;
  competition_id?: number;
  submission_version?: number;
  submitted_by?: string;
  submitted_at?: string;
  form?: {
    schema?: string;
    fields?: MultiFieldEntry[];
    values?: Record<string, unknown>;
  };
  fields?: MultiFieldEntry[];
  values?: Record<string, unknown>;
  artifacts?: MultiArtifact[];
}

const Submission: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const targetUsername = (searchParams.get('username') || '').trim();
  const [submission, setSubmission] = useState<SubmissionData | null>(null);
  const [competition, setCompetition] = useState<Competition | null>(null);
  useDocumentTitle(competition?.title ? `Submit · ${competition.title}` : undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMediaUrl, setSelectedMediaUrl] = useState<string | null>(null);
  const [multiManifest, setMultiManifest] = useState<MultiManifestData | null>(null);
  const [multiManifestLoading, setMultiManifestLoading] = useState(false);
  const [multiManifestError, setMultiManifestError] = useState('');
  const [consentStatus, setConsentStatus] = useState<string | null>(null);
  const [consentRank, setConsentRank] = useState<number | null>(null);
  const [consentLoading, setConsentLoading] = useState(false);
  const [consentUpdating, setConsentUpdating] = useState(false);
  const [consentError, setConsentError] = useState('');

  const competitionType = competition?.type?.toLowerCase();
  const isVideoCompetition = competitionType === 'video';
  const isVizCompetition = competitionType === 'viz';
  const isPdfCompetition = competitionType === 'pdf';
  const isHtmlCompetition = competitionType === 'html';
  const isGithubCompetition = competitionType === 'github';
  const isMultiCompetition = competitionType === 'multi';
  const shouldRenderMedia = isVideoCompetition || isVizCompetition || isPdfCompetition || isHtmlCompetition;

  // Fetch competition data
  useEffect(() => {
    if (!id) return;
    const fetchCompetition = async () => {
      try {
        const token = authUtils.getStoredToken();
        const response = await apiService.post(`/competitions/${id}`, {}, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setCompetition(response.data);
      } catch (err) {
        console.error('Error loading competition:', err);
      }
    };
    fetchCompetition();
  }, [id]);

  // Fetch last submission
  useEffect(() => {
    if (!id) return;
    const fetchLastSubmission = async () => {
      setLoading(true);
      setError('');
      try {
        const token = authUtils.getStoredToken();
        const response = await apiService.post(`/submissions/last_submission/${id}`, {}, {
          params: targetUsername ? { username: targetUsername } : undefined,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = response.data as SubmissionData;
        setSubmission(data);
        if (data?.manifest && typeof data.manifest === 'object') {
          setMultiManifest(data.manifest);
          setMultiManifestError('');
        } else {
          setMultiManifest(null);
        }
      } catch (err) {
        setError('Failed to load submission');
        console.error('Error fetching submission:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchLastSubmission();
  }, [id, targetUsername]);

  useEffect(() => {
    if (!isMultiCompetition) return;
    if (!submission?.url) return;
    if (multiManifest) return;

    const loadManifestFromUrl = async () => {
      try {
        setMultiManifestLoading(true);
        setMultiManifestError('');
        const response = await fetch(submission.url);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const parsed = (await response.json()) as MultiManifestData;
        setMultiManifest(parsed);
      } catch (err) {
        setMultiManifestError('Could not load multi submission details');
        console.error('Failed to load multi manifest JSON:', err);
      } finally {
        setMultiManifestLoading(false);
      }
    };

    loadManifestFromUrl();
  }, [isMultiCompetition, multiManifest, submission?.url]);

  useEffect(() => {
    if (!id || targetUsername) return;
    const fetchConsentState = async () => {
      setConsentLoading(true);
      setConsentError('');
      try {
        const token = authUtils.getStoredToken();
        const response = await apiService.get(`/evaluations/finalists/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const rows = Array.isArray(response?.data) ? (response.data as FinalistConsentEntry[]) : [];
        const mine = rows.find((entry) => Boolean(entry?.can_update_consent));
        if (mine) {
          setConsentStatus(mine.consent_status || 'pending');
          setConsentRank(Number.isFinite(mine.rank) ? mine.rank : null);
        } else {
          setConsentStatus(null);
          setConsentRank(null);
        }
      } catch (err) {
        setConsentStatus(null);
        setConsentRank(null);
        setConsentError('Could not load finalist consent status.');
        console.error('Error fetching finalist consent state:', err);
      } finally {
        setConsentLoading(false);
      }
    };
    fetchConsentState();
  }, [id, targetUsername]);

  const updateConsent = async (nextStatus: 'granted' | 'revoked') => {
    if (!id) return;
    setConsentUpdating(true);
    setConsentError('');
    try {
      const token = authUtils.getStoredToken();
      await apiService.post(
        `/evaluations/finalists/${id}/consent`,
        { consent_status: nextStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setConsentStatus(nextStatus);
    } catch (err) {
      setConsentError('Could not update consent. Please try again.');
      console.error('Error updating finalist consent:', err);
    } finally {
      setConsentUpdating(false);
    }
  };

  if (selectedMediaUrl && !isVideoCompetition && !isPdfCompetition) {
    window.open(selectedMediaUrl, '_blank');
    setSelectedMediaUrl(null);
  }

  const formatBytes = (size?: number): string => {
    if (!size || Number.isNaN(size)) return '-';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const renderManifestValue = (value: unknown): React.ReactNode => {
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return String(value);
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        return (
          <a href={trimmed} target="_blank" rel="noopener noreferrer">
            {trimmed}
          </a>
        );
      }
      return trimmed || '-';
    }
    if (Array.isArray(value)) {
      if (value.length === 0) return '-';
      return (
        <ul className="submission-multi-list">
          {value.map((entry, idx) => {
            if (typeof entry === 'string') {
              return <li key={`multi-value-${idx}`}>{entry}</li>;
            }
            if (entry && typeof entry === 'object') {
              const maybeObj = entry as { name?: string; value?: string };
              return <li key={`multi-value-${idx}`}>{maybeObj.name || maybeObj.value || JSON.stringify(maybeObj)}</li>;
            }
            return <li key={`multi-value-${idx}`}>{String(entry)}</li>;
          })}
        </ul>
      );
    }
    if (value && typeof value === 'object') return JSON.stringify(value);
    return '-';
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="submission-page">
          <LoadingSpinner text="Loading submission..." />
        </div>
      </>
    );
  }

  if (error || !submission) {
    return (
      <>
        <Header />
        <div className="submission-page">
          <button className="btn-back" onClick={() => window.history.back()}>
            &larr; Back
          </button>
          <div className="submission-empty" data-tour="submission-empty">
            <p>{error || 'No submission available.'}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="submission-page">
        <button className="btn-back" onClick={() => window.history.back()}>
          &larr; Back
        </button>

        {competition && (
          <div className="submission-header" data-tour="submission-header">
            <h1>{competition.title}</h1>
            <p className="submission-subtitle">Latest Submission: Version {submission.version} (out of max of {competition.number_of_submissions})</p>
          </div>
        )}

        {!targetUsername && (consentLoading || consentStatus) && (
          <div className="submission-consent" data-tour="submission-consent">
            <h3>Finalist Showcase Consent</h3>
            {consentLoading ? (
              <p>Checking finalist status...</p>
            ) : (
              <>
                <p>
                  {consentRank ? `You are a finalist (rank #${consentRank}). ` : ''}
                  Current sharing status: <strong>{consentStatus}</strong>.
                </p>
                <div className="submission-consent__actions">
                  <Button
                    variant="primary"
                    size="md"
                    onClick={() => updateConsent('granted')}
                    disabled={consentUpdating || consentStatus === 'granted'}
                  >
                    {consentUpdating && consentStatus !== 'granted' ? 'Updating...' : 'Grant sharing'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={() => updateConsent('revoked')}
                    disabled={consentUpdating || consentStatus === 'revoked'}
                  >
                    {consentUpdating && consentStatus !== 'revoked' ? 'Updating...' : 'Hide submission link'}
                  </Button>
                </div>
              </>
            )}
            {consentError && <p className="submission-consent__error">{consentError}</p>}
          </div>
        )}

        <div className="submission-instructions" data-tour="submission-instructions">
          {targetUsername ? (
            <p>
              Viewing latest submission for <strong>{submission.username || targetUsername}</strong>
              {submission.team_name ? (
                <> (team: <strong>{submission.team_name}</strong>)</>
              ) : null}
              . This mirrors the participant submission view.
            </p>
          ) : (
            <p>This is your latest submission for this competition. Use this to review how your submission will appear in the evaluation phase.</p>
          )}
        </div>

        <div className="submission-container" data-tour="submission-view">
          <div className="submission-card">
            <div className="submission-label">
              {targetUsername ? `${submission.username || targetUsername}'s Submission` : 'Your Submission'}
            </div>
            {isMultiCompetition ? (
              <div className="submission-multi-card">
                <a
                  href={submission.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="submission-pdf-link"
                >
                  Open raw submission manifest
                </a>
                {multiManifestLoading ? (
                  <LoadingSpinner size="sm" text="Loading submission package..." />
                ) : multiManifestError ? (
                  <p className="submission-empty">{multiManifestError}</p>
                ) : !multiManifest ? (
                  <p className="submission-empty">No manifest details available.</p>
                ) : (
                  <>
                    <div className="submission-multi-meta">
                      <span>Schema: {multiManifest.schema || 'ikompete.multi.v1'}</span>
                      {multiManifest.submitted_at && (
                        <span>Submitted: <RelativeTime value={multiManifest.submitted_at} /></span>
                      )}
                    </div>

                    {(() => {
                      const formFields = multiManifest.form?.fields || multiManifest.fields || [];
                      const formValues = multiManifest.form?.values || multiManifest.values || {};
                      if (!Array.isArray(formFields) || formFields.length === 0) {
                        return null;
                      }
                      return (
                        <div className="submission-multi-section">
                          <h3>Form Data</h3>
                          <div className="submission-multi-grid">
                            {formFields.map((field, idx) => {
                              const fieldValue = field.value ?? formValues[field.id || ''];
                              return (
                                <div key={`${field.id || 'field'}-${idx}`} className="submission-multi-item">
                                  <div className="submission-multi-item__label">{field.label || field.id || 'Field'}</div>
                                  <div className="submission-multi-item__value">{renderManifestValue(fieldValue)}</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}

                    <div className="submission-multi-section">
                      <h3>Uploaded Artifacts</h3>
                      {Array.isArray(multiManifest.artifacts) && multiManifest.artifacts.length > 0 ? (
                        <div className="submission-multi-artifacts">
                          {multiManifest.artifacts.map((artifact, idx) => (
                            <div key={`${artifact.object_key || artifact.stored_name || 'artifact'}-${idx}`} className="submission-multi-artifact">
                              <div>
                                <strong>{artifact.original_name || artifact.stored_name || 'File'}</strong>
                                <div className="submission-multi-artifact__meta">
                                  <span>{artifact.content_type || 'application/octet-stream'}</span>
                                  <span>{formatBytes(artifact.size_bytes)}</span>
                                </div>
                              </div>
                              {artifact.url ? (
                                <a href={artifact.url} target="_blank" rel="noopener noreferrer">
                                  Open
                                </a>
                              ) : (
                                <span>Unavailable</span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="submission-empty">No files uploaded in this package.</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            ) : shouldRenderMedia ? (
              isVideoCompetition ? (
                <div className="submission-video-wrapper">
                  <ReactPlayer
                    src={submission.url}
                    controls
                    width="100%"
                    height="100%"
                    className="submission-video-player"
                  />
                </div>
              ) : isPdfCompetition ? (
                <>
                  <a 
                    href={submission.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="submission-pdf-link"
                  >
                    📄 Open PDF in new tab
                  </a>
                  <div className="submission-pdf-wrapper">
                    <iframe
                      src={submission.url}
                      title="Your Submission"
                      className="submission-pdf-viewer"
                    />
                  </div>

                </>
              ) : isHtmlCompetition ? (
                <>
                  <a
                    href={submission.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="submission-pdf-link"
                  >
                    Open URL in new tab
                  </a>
                  <div className="submission-pdf-wrapper submission-html-wrapper">
                    <iframe
                      src={submission.url}
                      title="Your HTML Submission"
                      className="submission-pdf-viewer"
                    />
                  </div>
                </>
              ) : (
                <img
                  src={submission.url}
                  alt="Your Submission"
                  className="submission-image"
                  onClick={() => setSelectedMediaUrl(submission.url)}
                />
              )
            ) : (
              <Button
                variant="primary"
                size="lg"
                onClick={() => window.open(submission.url, '_blank')}
                className="submission-link-btn"
              >
                {isGithubCompetition ? 'Open GitHub repo' : 'Link to submission'}
              </Button>
            )}
          </div>
        </div>

        {shouldRenderMedia && !isVideoCompetition && !isPdfCompetition && !isHtmlCompetition && (
          <div className="submission-hint">
            <p>💡 Click on the image to view it in full size</p>
          </div>
        )}
      </div>
    </>
  );
};

export default Submission;
